// angular
import { NgOptimizedImage } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
// components
import {
  GalleryDialogComponent,
  getGalleryDialogConfig,
} from '@app/core/dialogs/gallery-dialog/gallery-dialog.component';
// services
import { DialogService } from '@app/core/shared/services/dialog.service';
// interfaces
import { IMedia } from '@app/shared/interfaces/media.interface';
// utils
import {
  formatCatalogFileSize,
  isCatalogImage,
  isCatalogPdf,
  partitionCatalogMedia,
} from '@app/+products/shared/utils/product-media.util';
import { resolveMediaFileUrl } from '@app/shared/utils/media.util';
import { environment } from '@environments/environment';
// pipes
import { ImageSourcePipe } from '@app/shared/pipes/image-source.pipe';

@Component({
  selector: 'pwj-product-catalogs',
  standalone: true,
  imports: [TranslateModule, NgOptimizedImage, ImageSourcePipe],
  providers: [ImageSourcePipe],
  templateUrl: './product-catalogs.component.html',
  styleUrl: './product-catalogs.component.scss',
})
export class ProductCatalogsComponent {
  catalogs = input.required<IMedia[]>();
  stackedBelowSpecs = input(false);

  private readonly _dialogService = inject(DialogService);
  private readonly _imageSourcePipe = inject(ImageSourcePipe);

  private readonly _partitioned = computed(() =>
    partitionCatalogMedia(this.catalogs()),
  );

  public pdfFiles = computed(() => this._partitioned().pdfs);
  public imageFiles = computed(() => this._partitioned().images);

  public catalogHref(media: IMedia): string {
    const url = media?.url;
    if (!url) {
      return '#';
    }
    return resolveMediaFileUrl(url, environment.baseUrl.file) || '#';
  }

  public catalogDisplayName(media: IMedia): string {
    const raw = media?.name?.trim();
    const fromName = raw ? raw.replace(/^\d+-/, '').trim() || raw : '';
    if (fromName) {
      return fromName.replace(/_/g, ' ');
    }
    const url = media?.url?.trim();
    if (!url) {
      return '';
    }
    const segment = url.split('?')[0].split('/').pop() ?? '';
    const stripped = segment.replace(/^\d+-/, '').trim();
    return (stripped || segment).replace(/_/g, ' ');
  }

  public catalogMetaLabel(media: IMedia): string {
    const size = formatCatalogFileSize(media.size);
    if (isCatalogPdf(media)) {
      return size ? `PDF • ${size}` : 'PDF';
    }
    if (isCatalogImage(media)) {
      return size ? `Image • ${size}` : 'Image';
    }
    return size || '';
  }

  public catalogImageSrc(media: IMedia): string {
    const path =
      media.cropped_url || media.thumbnail || media.url || '';
    return this._imageSourcePipe.transform(path) ?? '';
  }

  public gridCardVariant(index: number): 'slate' | 'accent' | 'dark' {
    const variants: Array<'slate' | 'accent' | 'dark'> = [
      'slate',
      'accent',
      'dark',
    ];
    return variants[index % variants.length];
  }

  public openImagePreview(media: IMedia, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.catalogImageSrc(media)) {
      return;
    }

    this._dialogService.open(
      GalleryDialogComponent,
      getGalleryDialogConfig({
        title: 'global.gallery',
        mediaData: this.imageFiles(),
        useOriginalUrl: false,
        initialMedia: media,
      }),
    );
  }
}
